import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, Star, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { displayPrice, formatDate } from '../lib/format';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function VehiclesAdmin() {
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== 'all') params.status = status;
    api
      .get('/admin/vehicles', { params })
      .then((res) => setVehicles(res.data.vehicles))
      .catch(() => toast.error('Failed to load vehicles'))
      .finally(() => setLoading(false));
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const toggleStatus = async (v) => {
    const newStatus = v.status === 'Available' ? 'Sold' : 'Available';
    try {
      await api.patch(`/admin/vehicles/${v.id}/status`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const toggleFeatured = async (v) => {
    try {
      await api.put(`/admin/vehicles/${v.id}`, { featured: !v.featured });
      toast.success(v.featured ? 'Removed from featured' : 'Marked as featured');
      load();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/vehicles/${deleteTarget.id}`);
      toast.success('Vehicle deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Failed to delete vehicle');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl tracking-wide text-[#24135F]">VEHICLES</h1>
        <Button
          asChild
          data-testid="admin-add-vehicle-button"
          className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-10 px-5 text-xs font-bold tracking-wider transition-colors duration-150"
        >
          <Link to="/admin/vehicles/new">
            <Plus className="h-4 w-4 mr-1" /> ADD VEHICLE
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Input
            data-testid="admin-vehicles-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, make, stock no..."
            className="h-10 rounded-lg pr-9 bg-white focus-visible:ring-2 focus-visible:ring-[#E4002B]"
          />
          <Search className="absolute right-3 top-2.5 h-4 w-4 text-[#53565A]/70" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-vehicles-status-filter" className="h-10 rounded-lg w-[150px] bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Available">Available</SelectItem>
            <SelectItem value="Sold">Sold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#D9D9D6] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-16" data-testid="admin-vehicles-empty">
            <Truck className="h-10 w-10 text-[#D9D9D6] mx-auto mb-3" />
            <p className="text-sm text-[#53565A]">No vehicles found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-vehicles-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicles.map((v) => (
                  <TableRow key={v.id} data-testid="admin-vehicle-row" className="hover:bg-[#D9D9D6]/15">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-16 rounded-md overflow-hidden bg-[#D9D9D6]/30 shrink-0">
                          {v.thumbnail ? (
                            <img src={v.thumbnail} alt="" className="h-full w-full object-cover" loading="lazy" />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-[#D9D9D6]">
                              <Truck className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#24135F]">{v.title}</p>
                          <p className="text-xs text-[#53565A]/70">{v.stock_number || v.vehicle_type}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{displayPrice(v)}</TableCell>
                    <TableCell>
                      <button
                        data-testid="admin-vehicle-status-toggle"
                        onClick={() => toggleStatus(v)}
                        title={`Click to mark as ${v.status === 'Available' ? 'Sold' : 'Available'}`}
                        className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors duration-150 ${
                          v.status === 'Available'
                            ? 'border-[#24135F]/20 bg-[#24135F]/5 text-[#24135F] hover:bg-[#24135F]/10'
                            : 'border-[#E4002B]/25 bg-[#E4002B]/5 text-[#E4002B] hover:bg-[#E4002B]/10'
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${v.status === 'Available' ? 'bg-[#24135F]' : 'bg-[#E4002B]'}`} />
                        {v.status}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button
                        data-testid="admin-vehicle-featured-toggle"
                        onClick={() => toggleFeatured(v)}
                        className="p-1.5 rounded-lg hover:bg-[#D9D9D6]/30 transition-colors duration-150"
                        title={v.featured ? 'Remove from featured' : 'Mark as featured'}
                      >
                        <Star className={`h-4 w-4 ${v.featured ? 'fill-[#E4002B] text-[#E4002B]' : 'text-[#D9D9D6]'}`} />
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-[#53565A]">{formatDate(v.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" data-testid="admin-vehicle-edit-button" className="h-8 w-8 hover:bg-[#D9D9D6]/30">
                          <Link to={`/admin/vehicles/${v.id}/edit`}>
                            <Pencil className="h-4 w-4 text-[#53565A]" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid="admin-vehicle-delete-button"
                          onClick={() => setDeleteTarget(v)}
                          className="h-8 w-8 hover:bg-[#E4002B]/5"
                        >
                          <Trash2 className="h-4 w-4 text-[#E4002B]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-vehicle-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vehicle?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.title}" and all its images. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-vehicle-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-vehicle-confirm-button"
              onClick={handleDelete}
              className="bg-[#E4002B] hover:bg-[#E4002B]/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
